import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, SITE } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Phone, Mail, MapPin } from "lucide-react";

const BUYER_INTERESTS = [
  { value: "buying", label: "Buying a home" },
  { value: "events", label: "Getting on your events list" },
  { value: "selling", label: "Selling a home" },
  { value: "coaching", label: "Agent coaching" },
  { value: "escrow", label: "Escrow questions" },
  { value: "general", label: "General inquiry" },
];

const AGENT_INTERESTS = [
  { value: "coaching", label: "Coaching / mentorship" },
  { value: "escrow", label: "Escrow & netsheet support" },
  { value: "team", label: "Joining your sales team" },
  { value: "deal-room", label: "Deal Room access" },
  { value: "openhouse", label: "Open house marketing plan" },
  { value: "events", label: "Getting on your events list" },
  { value: "general", label: "General inquiry" },
];

const AGENT_COPY = {
  overline: "Agent to agent",
  title: "Let's talk shop.",
  intro:
    "We love hearing from fellow agents. Whether you're curious about coaching, want to send escrow our way, or are exploring a move to our team — drop a note and we'll be in touch personally.",
  placeholder:
    "Tell us where you are in your business and what you're looking for…",
};

const BUYER_COPY = {
  overline: "Connect",
  title: "Let's start the conversation.",
  intro:
    "A quick form. A real reply. Whether you're three months from buying, ready to list, or curious about coaching — we'd love to hear from you.",
  placeholder: "Tell me a bit about what you're working on…",
};

export default function Contact() {
  const [params] = useSearchParams();
  const isAgent = params.get("for") === "agents";
  const interests = isAgent ? AGENT_INTERESTS : BUYER_INTERESTS;
  const copy = isAgent ? AGENT_COPY : BUYER_COPY;
  const requested = params.get("interest");
  const defaultInterest = interests.some((i) => i.value === requested) ? requested : interests[0].value;

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: defaultInterest,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      toast.success("Thanks — We'll reach out shortly.");
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="container-edge pt-16 pb-8">
        <p className="overline" data-testid="contact-overline">{copy.overline}</p>
        <h1
          className="mt-6 text-5xl md:text-6xl lg:text-7xl max-w-3xl"
          style={{ fontFamily: "Playfair Display, serif" }}
          data-testid="contact-title"
        >
          {copy.title}
        </h1>
        <p className="mt-6 text-soft max-w-2xl">{copy.intro}</p>
      </section>

      <section className="section pt-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 bg-paper border border-soft p-8 md:p-12">
            {sent ? (
              <div className="py-10" data-testid="contact-sent">
                <p className="overline">Thank you</p>
                <h2 className="mt-4 text-4xl" style={{ fontFamily: "Playfair Display, serif" }}>
                  Your note is on its way.
                </h2>
                <p className="mt-4 text-soft">Sonia replies personally — usually within a business day.</p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-8" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div>
                    <label className="overline">Name</label>
                    <input
                      className="input-editorial mt-2"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your full name"
                      data-testid="contact-name"
                      required
                    />
                  </div>
                  <div>
                    <label className="overline">Email</label>
                    <input
                      type="email"
                      className="input-editorial mt-2"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@email.com"
                      data-testid="contact-email"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div>
                    <label className="overline">Phone (optional)</label>
                    <input
                      className="input-editorial mt-2"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="805-555-0123"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label className="overline">I'm interested in</label>
                    <select
                      className="input-editorial mt-2"
                      value={form.interest}
                      onChange={update("interest")}
                      data-testid="contact-interest"
                    >
                      {interests.map((i) => (
                        <option key={i.value} value={i.value}>{i.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="overline">Message</label>
                  <textarea
                    className="input-editorial mt-2 min-h-[140px]"
                    value={form.message}
                    onChange={update("message")}
                    placeholder={copy.placeholder}
                    data-testid="contact-message"
                  />
                </div>
                <button type="submit" disabled={submitting} className="btn-primary w-full sm:w-auto" data-testid="contact-submit">
                  {submitting ? (
                    <><Loader2 size={16} className="animate-spin mr-2" /> Sending…</>
                  ) : (
                    "Send message"
                  )}
                </button>
              </form>
            )}
          </div>
          <div className="lg:col-span-5 space-y-10">
            <div>
              <p className="overline">Or reach out directly</p>
              <ul className="mt-6 space-y-5 text-base">
                <li className="flex items-center gap-4">
                  <Phone size={16} className="text-sand" />
                  <a href={SITE.teamPhoneHref} className="hover:text-sand transition-colors" data-testid="contact-phone-link">{SITE.teamPhone}</a>
                </li>
                <li className="flex items-center gap-4">
                  <Mail size={16} className="text-sand" />
                  <a href={SITE.emailHref} className="hover:text-sand transition-colors break-all" data-testid="contact-email-link">{SITE.email}</a>
                </li>
                <li className="flex items-center gap-4">
                  <MapPin size={16} className="text-sand" />
                  <span>{SITE.serviceArea}</span>
                </li>
              </ul>
            </div>
            <div className="bg-linen p-8">
              <p className="overline">Licensed in</p>
              <h3 className="mt-3 text-2xl" style={{ fontFamily: "Playfair Display, serif" }}>California</h3>
              <p className="mt-3 text-soft text-sm">{SITE.dre} · Spotlight Realtor Network · Keller Williams California</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
